import { Link } from "react-router-dom";
import { ArrowRight, Anchor } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import { SERVICES } from "../data/content";
import { useT, useLang } from "../i18n/LanguageContext";
import { localizeServices } from "../i18n/content_it";

export default function Services() {
  const t = useT();
  const { lang } = useLang();
  const services = localizeServices(SERVICES, lang);
  return (
    <div className="pt-28">
      <Seo
        title={t("seo.services.title")}
        description={t("seo.services.desc")}
        locale={lang === "it" ? "it_IT" : "en_US"}
        path="/services"
      />

      <section className="px-6 md:px-12 max-w-[1600px] mx-auto">
        <Reveal>
          <p className="overline mb-4">{t("services.overline")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light leading-none max-w-4xl">
            {t("services.title")}
          </h1>
          <p className="text-white/60 mt-8 max-w-2xl leading-relaxed">
            {t("services.intro")}
          </p>
        </Reveal>
      </section>

      <section className="mt-24 space-y-24 md:space-y-32">
        {services.map((s, i) => (
          <Reveal key={s.id}>
            <div className={`px-6 md:px-12 max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${i % 2 ? "" : ""}`}>
              <div className={`lg:col-span-7 relative h-[340px] md:h-[480px] overflow-hidden group ${i % 2 ? "lg:order-2" : ""}`}>
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
              <div className="lg:col-span-5">
                <span className="font-serif text-5xl text-[#C5A059]/70">0{i + 1}</span>
                <h2 id={s.id} className="font-serif text-3xl md:text-4xl mt-4 mb-5 scroll-mt-28">{s.title}</h2>
                <p className="text-white/70 leading-relaxed">{s.long}</p>
                {s.id === "shore-excursions" ? (
                  <Link to="/cruise-tours" data-testid={`service-cta-${s.id}`} className="group inline-flex items-center gap-3 mt-8 border border-[#C5A059] text-[#C5A059] px-7 py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors">
                    <Anchor size={15} /> {t("services.viewItineraries")}
                  </Link>
                ) : (
                  <Link to="/#contact" data-testid={`service-cta-${s.id}`} className="group inline-flex items-center gap-3 mt-8 border border-white/25 text-white px-7 py-3 uppercase tracking-[0.2em] text-xs hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
                    {t("services.enquire")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="px-6 md:px-12 max-w-[1600px] mx-auto mt-32 mb-8">
        <Reveal>
          <div className="border border-[#C5A059]/30 bg-[#121212] p-10 md:p-16 text-center">
            <h2 className="font-serif text-3xl md:text-5xl font-light">{t("services.ctaTitle")}</h2>
            <p className="text-white/60 mt-5 max-w-xl mx-auto">{t("services.ctaText")}</p>
            <Link to="/cruise-tours" data-testid="services-tours-cta" className="inline-flex items-center gap-3 mt-8 bg-[#C5A059] text-[#0A0A0A] px-8 py-4 uppercase tracking-[0.2em] text-sm hover:bg-[#D4AF37] transition-colors">
              {t("services.ctaButton")} <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
